// Angular
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
// RxJS
import { Observable, of } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';
// Models
import { OrderModel } from '../_models/orders/order.model';

import { environment } from 'src/environments/environment';
import { OrderBoxModel } from '../_models/orders/order-boxes/order-box.model';
import { OrderBoxItemModel } from '../_models/orders/order-boxes/order-box-item.model';
import { OrderCount, OrdersFetchFilters } from './self-ship/self-ship-orders.service';
import { OrdersFiltersModel } from '../_models/self-ship/orders/orders-filters.model';
import OrdersFiltersState from '../states/orders/orders-filters/orders-filters.state';
import { OrderOutboundDTO } from '../_DTOs/outbound/order/order-outbound.dto';
import { StatesHelper } from '../states/states.helper';
import { FulFilledOrderDTO } from '../_models/fulfillment-dto.model';
import { BulkPackItemsToBoxPayload } from '../_models/orders/order-boxes/bulk-order-box.model';
import { IndividualPickTicket } from '../_models/orders/order-items/individual-pick-ticket.models';
import { MassPickTicket } from '../_models/orders/order-items/mass-pick-ticket.models';
import { OrderBoxesRateByOrderIdResponse, OrderBoxRate } from '../_models/orders/order-boxes/orderBoxesRateByOrderId.model';
import { ApproveOrderBoxesRateResponse } from '../_models/orders/order-boxes/approveOrderBoxesRate.model';

const API_ORDERS_URL = environment.baseUrl + '/orders';
const API_ORDER_BOXES_URL = environment.baseUrl + '/orderBoxes';

@Injectable()
export class OrdersService {

    constructor(
        private http: HttpClient,
        private ordersFiltersState: OrdersFiltersState
    ) {
    }

    // In-flight requests and short-lived cache to avoid duplicate calls
    private inFlightOrderBoxRates: Map<number, Observable<OrderBoxRate[]>> = new Map();
    private cachedOrderBoxRates: Map<number, { timestamp: number; data: OrderBoxRate[] } > = new Map();
    private readonly orderBoxRatesTtlMs = 10_000; // 10 seconds

    public getOrdersFetchFilterParams(): OrdersFetchFilters {
        const filters: OrdersFiltersModel = this.ordersFiltersState.snapshot;
        const payload = {
            fromDate: filters.dateRange.fromDate.toUTCString(),
            toDate: filters.dateRange.toDate.toUTCString(),
            status: filters.selectedStatusTab
        };
        if (filters.customer) Object.assign(payload, { customer: filters.customer });
        return payload;
    }

    public getTotalOrdersCount(filters: OrdersFetchFilters): Observable<OrderCount> {
        return this.http.get<OrderCount>(`${API_ORDERS_URL}/getTotalOrdersCount`, { params: filters });
    }

    public getOrders(filters: OrdersFetchFilters, pageId: number = 1, pageSize: number = 20): Observable<OrderModel[]> {
        const payload: any = filters;
        payload.pageSize = pageSize;
        payload.pageId = pageId;
        return (this.http.get(`${API_ORDERS_URL}/getOrders`, { params: payload }) as Observable<OrderModel[]>);
    }

    public fetch(filters: OrdersFetchFilters): Observable<OrderModel[]> {
        return (this.http.get(`${API_ORDERS_URL}`, { params: filters }) as Observable<OrderModel[]>);
    }

    public fetchSingleOrder(orderId: number): Observable<OrderModel> {
        return this.http.get<OrderModel>(`${API_ORDERS_URL}/${orderId}`);
    }

    public fetchSingleOrderDetail(orderId: number, includeItems: boolean = true, includeShipments: boolean = false): Observable<OrderModel> {
        const params: any = {};
        if (includeItems) params.includeItems = true;
        if (includeShipments) params.includeShipments = true;
        return this.http.get<OrderModel>(`${API_ORDERS_URL}/${orderId}`, { params });
    }

    public fetchOrderItems(orderId: number) {
        return this.http.get(`${API_ORDERS_URL}/${orderId}/items`).pipe(
            tap((items: any[]) => {
                const order = StatesHelper.ordersEntitiesState.entities[orderId];
                if (!order) return;
                StatesHelper.ordersEntitiesState.updateOne({ id: orderId, changes: { ...order, items } as any });
            })
        );
    }

    public fetchOrderBoxes(orderId: number) {
        return this.http.get(`${API_ORDERS_URL}/${orderId}/boxes`).pipe(
            tap((boxes: any[]) => StatesHelper.ordersEntitiesState.upsertOrderBoxes(...(boxes as any)))
        );
    }

    public getOrderDetail(storeId: number, orderNumber: string): any {
        return this.http.get(`${API_ORDERS_URL}/${storeId}/${orderNumber}`);
    }

    public getOrderDetailByreimbursementId(storeId: number, orderNumber: string): any {
        return this.http.get(`${API_ORDERS_URL}/${storeId}/GetOrderDetailByreimbursementId/${orderNumber}`);
    }

    public create(outboundOrder: OrderOutboundDTO): Observable<any> {
        return this.http.post(`${API_ORDERS_URL}`, outboundOrder);
    }

    // public deleteOrder(orderId: number) {
    //   return this.http.delete(`${API_ORDERS_URL}/${orderId}`);
    // }

    public update(outboundOrder: OrderOutboundDTO): Observable<any> {
        return this.http.put(`${API_ORDERS_URL}/${outboundOrder.id}`, outboundOrder);
    }

    public updateOrderItems(payload: { orderItemIds: number[], warehouseId?: number, markAsShipped?: boolean; }): Observable<unknown> {
        return this.http.put(`${API_ORDERS_URL}/${this.updateOrderItems.name}`, payload);
    }

    public createOrderBoxes(data: { orderId: number, boxes: OrderBoxModel[], groupId?: number, groupName?: string; }) {
        return this.http.post<OrderBoxModel[]>(`${API_ORDER_BOXES_URL}`, data);
    }

    public getIndividualPickTicket(orderIds: number[]) {
        return this.http.post<IndividualPickTicket[]>(`${API_ORDERS_URL}/getOrdersPickTicket`, orderIds);
    }

    public getMassPickTicket(orderIds: number[]) {
        return this.http.post<MassPickTicket>(`${API_ORDERS_URL}/getMassPickTicket`, orderIds);
    }

    public retryAutoAssignBoxesToOrder(orderIds: number[]) {
        return this.http.post<{ totalSuccess: number, totalFailed: number; }>(`${API_ORDER_BOXES_URL}/retryAutoAssignBoxesToOrder`, orderIds);
    }

    public createBulkOrdersBoxes(requestData: { orderIds: number[], box: OrderBoxModel, overwriteExistingBoxes: boolean; }) {
        return this.http.post<OrderModel[]>(`${API_ORDER_BOXES_URL}/createBulk`, requestData);
    }

    public updateOrderBox(box: OrderBoxModel) {
        return this.http.put(`${API_ORDER_BOXES_URL}`, box);
    }

    public packItemToBox(orderBoxItem: OrderBoxItemModel) {
        return this.http.put(`${API_ORDER_BOXES_URL}/packItemToBox`, orderBoxItem);
    }

    public selectOrderBoxesForRates(data: { warehouseId: number, shipFromAddressId: number, orderBoxIds: number[]; }) {
        return this.http.put(`${API_ORDER_BOXES_URL}/selectOrderBoxesForRates`, data)
            .pipe(
                tap((result: OrderBoxModel[]) => StatesHelper.ordersEntitiesState.upsertOrderBoxes(...result))
            );
    }

    public getfulfillments() {
        return this.http.get<any>(`${API_ORDERS_URL}/Fulfilments`);
    }

    public downloadOrders(all) {
        return this.http.get(`${API_ORDERS_URL}/downloadOrders`, { responseType: 'blob', params: { all } });
    }

    public getOrderBoxesRateByOrderId(orderId: number) {
        return this.http.get<OrderBoxesRateByOrderIdResponse[]>(`${API_ORDER_BOXES_URL}/GetOrderBoxesRateByOrderId/${orderId}`);
    }

    public getOrderBoxRatesByOrderBoxId(orderBoxId: number) {
        if (!orderBoxId) {
            return of([] as OrderBoxRate[]);
        }

        // Serve from cache if fresh
        const cached = this.cachedOrderBoxRates.get(orderBoxId);
        const now = Date.now();
        if (cached && now - cached.timestamp < this.orderBoxRatesTtlMs) {
            return of(cached.data);
        }

        // Return existing in-flight observable if present
        const existing = this.inFlightOrderBoxRates.get(orderBoxId);
        if (existing) {
            return existing;
        }

        const request$ = this.http
            .get<OrderBoxRate[]>(`${API_ORDER_BOXES_URL}/GetOrderBoxsRatesByOrderBoxId/${orderBoxId}`)
            .pipe(
                tap((data) => this.cachedOrderBoxRates.set(orderBoxId, { timestamp: Date.now(), data: data || [] })),
                shareReplay(1)
            );

        this.inFlightOrderBoxRates.set(orderBoxId, request$);

        // Clean up in-flight entry when the request completes/errors
        request$.subscribe({
            next: () => this.inFlightOrderBoxRates.delete(orderBoxId),
            error: () => this.inFlightOrderBoxRates.delete(orderBoxId),
            complete: () => this.inFlightOrderBoxRates.delete(orderBoxId)
        });

        return request$;
    }

    public setOrderBoxesDefaultRate(orderBoxId: number, orderBoxRateId: number) {
        return this.http.post<OrderBoxesRateByOrderIdResponse[]>(`${API_ORDER_BOXES_URL}/setOrderBoxesDefaultRate`, [{ orderBoxId, orderBoxRateId }]);
    }

    public getReservedOrders(productId: number = null) {
        let params: any = {};
        if (productId) {
            params.productId = productId;
        }
        return this.http.get<any>(`${API_ORDERS_URL}/ReservedOrders`, { params: params });
    }

    public deleteOrderBox(id: number) {
        return this.http.delete(`${API_ORDER_BOXES_URL}/${id}`);
    }

    public syncWithMarketPlaceOrder(orderId: number): Observable<any> {
        return this.http.put(`${API_ORDERS_URL}/SyncOrderWithMarketPlace/${orderId}`, null);
    }

    public syncWithMarketPlaceOrders(orderIds: number[]): Observable<any> {
        return this.http.post<ApproveOrderBoxesRateResponse[]>(`${API_ORDERS_URL}/SyncOrdersWithMarketPlace`, orderIds);
    }

    public updateAdditionalFees(storeId: number, orderNumber: string, additionalFees: number = undefined): Observable<unknown> {
        return this.http.patch(`${API_ORDERS_URL}/${storeId}/${orderNumber}/UpdateAdditionalFees`, null,
            { params: { additionalFees: additionalFees } });
    }

    public createFulfillmentOrder(payload: FulFilledOrderDTO): Observable<unknown> {
        return this.http.post(`${API_ORDERS_URL}/createFulfillmentOrder`, payload);
    }

    public bulkPackItemsToBoxes(payload: BulkPackItemsToBoxPayload[]): Observable<unknown> {
        return this.http.put(`${API_ORDER_BOXES_URL}/bulkPackItemsToBoxes`, { orderBoxItems: payload });
    }

    public updateOrdersStatus(orderIds: number[], orderStatus: string, changeToUnpickOrUnPack: boolean): Observable<unknown> {
        return this.http.post(`${API_ORDERS_URL}/updateOrdersStatus`, { orderIds, orderStatus, changeToUnpickOrUnPack });
    }

    public approveOrderBoxesRate(orderIds: number[]): Observable<ApproveOrderBoxesRateResponse[]> {
        return this.http.post<ApproveOrderBoxesRateResponse[]>(`${API_ORDERS_URL}/approveOrderBoxesRate`, orderIds);
    }

    public retryAutoAssignRatesToOrder(orderIds: number[]): Observable<any> {
        return this.http.post(`${API_ORDERS_URL}/RetryAutoAssignRatesToOrder`, orderIds);
    }

    public retryAutoAssignRatesToOrderBoxes(orderBoxIds: number[]): Observable<any> {
        return this.http.post(`${API_ORDER_BOXES_URL}/RetryAutoAssignRatesToOrderBoxes`, orderBoxIds);
    }

    public checkLabelsExistForOrders(orderIds: number[]): Observable<boolean> {
        return this.http.post<boolean>(`${API_ORDERS_URL}/CheckLabelExistForOrders`, orderIds);
    }

    // Returns only orders (and their shipping details) that already have at least one shipping label
    public getOrdersWithLabels(orderIds: number[]): Observable<any[]> {
        return this.http.post<any[]>(`${API_ORDERS_URL}/GetOrdersLabels`, orderIds);
    }
}
